import { UUID } from "crypto";

import { documentMediaApi } from "../../api/src/apiClient";
import { uploadFileToS3 } from "../../api/src/externalApiClient";
import { S3UploadItem } from "../../api/src/apiContracts/external";
import { MediaKind } from "./types";
import { getFileExtension } from "./mime";

export interface MediaUploadItem {
  file: File;
  mediaKind: MediaKind;
}

export interface MediaUploadItemResponse {
  mediaUuid: UUID;
  uploadUrl: string;
  contentType: string;
  extension: string;
  file: File;
  downloadUrl?: string;
}

/**
 * Utility function to upload media files to S3 using pre-signed URLs
 * @param mediaItems - Array of media items with files and their kinds
 * @param documentUuid - UUID of the document to associate uploads with
 * @param onProgress - Callback to track upload progress (uploaded count, total count)
 * @returns Promise that resolves to array of successfully uploaded media metadata
 */
export async function uploadMediaToS3WithPresignedUrls(
  mediaItems: MediaUploadItem[],
  documentUuid: UUID,
  onProgress: (uploaded: number, total: number) => void,
): Promise<MediaUploadItemResponse[]> {
  if (!mediaItems.length || !documentUuid) {
    console.error("Failed to upload file: mediaItems or documentUuid is empty");
    return [];
  }

  try {
    // Separate media items by kind
    const imageItems = mediaItems.filter((item) => item.mediaKind === MediaKind.Image);
    const videoItems = mediaItems.filter((item) => item.mediaKind === MediaKind.Video);

    // Generate pre-signed URLs
    const uploadRequest = {
      imagesToUpload: imageItems.map((item) => ({
        extension: getFileExtension(item.file),
        sizeInBytes: item.file.size,
      })),
      videosToUpload: videoItems.map((item) => ({
        extension: getFileExtension(item.file),
        sizeInBytes: item.file.size,
      })),
    };

    const uploadResponse = await documentMediaApi.generateMediaUploadUrls(documentUuid, uploadRequest);

    // Create upload items and metadata responses
    const allUploadMetadata: MediaUploadItemResponse[] = [
      ...uploadResponse.imageUploadUrls.map((item, index) => ({
        mediaUuid: item.mediaUuid,
        uploadUrl: item.uploadUrl,
        contentType: imageItems[index]!.file.type,
        file: imageItems[index]!.file,
        extension: getFileExtension(imageItems[index]!.file),
      })),
      ...uploadResponse.videoUploadUrls.map((item, index) => ({
        mediaUuid: item.mediaUuid,
        uploadUrl: item.uploadUrl,
        contentType: videoItems[index]!.file.type,
        file: videoItems[index]!.file,
        extension: getFileExtension(videoItems[index]!.file),
      })),
    ];

    const uploadItems: S3UploadItem[] = [
      ...uploadResponse.imageUploadUrls.map((item, index) => ({
        uploadUrl: item.uploadUrl,
        file: imageItems[index]!.file,
      })),
      ...uploadResponse.videoUploadUrls.map((item, index) => ({
        uploadUrl: item.uploadUrl,
        file: videoItems[index]!.file,
      })),
    ];

    // Upload to S3 with progress tracking and collect successful uploads
    const successfulImages: MediaUploadItemResponse[] = [];
    const successfulVideos: MediaUploadItemResponse[] = [];
    let uploadedCount = 0;
    const total = uploadItems.length;

    const uploadPromises = uploadItems.map(async (item, index) => {
      try {
        await uploadFileToS3(item.uploadUrl, item.file);
        uploadedCount++;
        onProgress(uploadedCount, total);

        const uploadMetadata = allUploadMetadata[index]!;
        // Images come first in the arrays, then videos
        if (index < imageItems.length) {
          successfulImages.push(uploadMetadata);
        } else {
          successfulVideos.push(uploadMetadata);
        }
        return true;
      } catch (error) {
        console.error("Failed to upload file:", error);
        throw error;
      }
    });

    await Promise.all(uploadPromises);

    // Generate download URLs for successful uploads
    if (successfulImages.length > 0 || successfulVideos.length > 0) {
      const downloadRequest = {
        imagesToDownload: successfulImages.map((upload) => ({
          mediaUuid: upload.mediaUuid,
          extension: upload.extension,
        })),
        videosToDownload: successfulVideos.map((upload) => ({
          mediaUuid: upload.mediaUuid,
          extension: upload.extension,
        })),
      };

      try {
        const downloadResponse = await documentMediaApi.generateMediaDownloadUrls(documentUuid, downloadRequest);

        // Create lookup maps for download URLs
        const imageDownloadMap = new Map(
          downloadResponse.imageDownloadUrls.map((item) => [item.mediaUuid, item.downloadUrl]),
        );
        const videoDownloadMap = new Map(
          downloadResponse.videoDownloadUrls.map((item) => [item.mediaUuid, item.downloadUrl]),
        );

        // Add download URLs to successful uploads
        successfulImages.forEach((upload) => {
          upload.downloadUrl = imageDownloadMap.get(upload.mediaUuid);
        });
        successfulVideos.forEach((upload) => {
          upload.downloadUrl = videoDownloadMap.get(upload.mediaUuid);
        });
      } catch (error) {
        console.error("Failed to generate download URLs:", error);
        // Continue without download URLs rather than failing the entire upload
      }
    }

    return [...successfulImages, ...successfulVideos];
  } catch (error) {
    console.error("Error uploading media to S3:", error);
    throw error;
  }
}

/**
 * Utility function to convert a media file to base64 format
 * @param file - Media file to convert
 * @returns Promise that resolves to base64 data and file type
 */
export function createMediaDataFromFile(file: File): Promise<{ fileType: string; data: string }> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = () => {
      const result = reader.result as string;
      const base64Data = result.split(",")[1];
      const fileType = file.type;
      resolve({
        fileType,
        data: base64Data!,
      });
    };

    reader.onerror = (error) => reject(error);
    reader.readAsDataURL(file);
  });
}

/**
 * Check if a file is a supported media type
 * @param file - File to check
 * @param supportedTypes - Array of supported MIME type prefixes (e.g., ['image/', 'video/'])
 * @returns boolean indicating if file type is supported
 */
export function isSupportedMediaType(file: File, supportedTypes: string[]): boolean {
  return supportedTypes.some((type) => file.type.startsWith(type));
}
